import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { CheckIcon, StarIcon, ClockIcon, ShoppingCartIcon } from 'lucide-react';
import { useMetaPixel, getActiveCtaPixelIds } from '../hooks/useMetaPixel';
import { useMidnightCountdown } from '../hooks/useMidnightCountdown';
import { LegalLinks } from '../components/LegalLinks';
import { ScarcityCountdown, START_CLAIMED, MAX_CLAIMED } from '../components/ScarcityCountdown';
import { SalesNotifications } from '../components/SalesNotifications';
// CTA checkout link — Commas checkout with the fixed affiliate code.
const OFFER_147_CHECKOUT_URL = 'https://commas.com/checkout/gQ88lpN7PKuMEe';
// Floating WhatsApp contact — number (786) 935-2183 (US +1).
const WHATSAPP_LINK = 'https://wa.me/17869352183?text=' + encodeURIComponent('¿Cómo podemos ayudarte?');
/**
 * /demo — a focused, native-feeling page (looks like an in-app browser view):
 * founder trust row, a "PASO #1" headline, a single PandaVideo, and a
 * social-proof block. The rest of the steps unlock on a 4-minute time gate.
 */
const PANDA_BASE = 'https://player-vz-cf0b871f-7fe.tv.pandavideo.com/embed/?v=';
const PANDA_ID = '0a7e74ce-c106-44a5-9484-3931fa75bddc';
const PANDA_SRC = PANDA_BASE + PANDA_ID;
// PASO #2 video — "Dentro De La Academia", shown under the first video.
const PASO2_ID = 'e093fc45-6c0c-4b4f-a837-c72e72759c0b';
const PASO2_SRC = PANDA_BASE + PASO2_ID;
// PASO #3 video — "Costo operativo Del Negocio", shown under PASO #2.
const PASO3_ID = '97f1c09e-eb21-4fdf-9cc5-92d43ef5dc47';
const PASO3_SRC = PANDA_BASE + PASO3_ID;
// Testimonial video shown beneath the main video.
const TESTIMONIAL_ID = '423503d6-113f-461a-aef3-6b9782aadebb';
const TESTIMONIAL_SRC = PANDA_BASE + TESTIMONIAL_ID;
// Cookie that remembers a visitor who has already unlocked the full demo page
// (the 4-minute time gate elapsed). On their next visit the page loads fully
// unlocked instead of gating them behind the countdown again.
const DEMO_UNLOCKED_COOKIE = 'demo_unlocked';
const COOKIE_MAX_AGE_DAYS = 30;
function hasUnlockedCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some(c => c.startsWith(`${DEMO_UNLOCKED_COOKIE}=1`));
}
function setUnlockedCookie() {
  if (typeof document === 'undefined') return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${DEMO_UNLOCKED_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
}
// Social-proof avatar faces (stacked member row).
const MEMBER_AVATARS = ['https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/54.jpg'];
// Reusable rounded video card so every step shares the same framing.
function VideoCard({
  id,
  src,
  title,
  priority = false
}: {
  id: string;
  src: string;
  title: string;
  priority?: boolean;
}) {
  return <div className="relative w-full overflow-hidden rounded-2xl bg-black shadow-[0_12px_40px_rgba(0,0,0,0.18)]" data-id="element-431" data-mp="2zj6r4l86mzgd">
      <div className="relative w-full" style={{
      paddingTop: '56.25%'
    }} data-id="element-432" data-mp="1ezl3eqlf3xmi">
        <iframe id={`panda-${id}`} src={src} title={title} className="absolute inset-0 w-full h-full border-0" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowFullScreen {...priority ? {
        fetchPriority: 'high' as any
      } : {
        loading: 'lazy' as const
      }} data-id="element-433" data-mp="1js4g7lca6oq9" />
      </div>
    </div>;
}
export function DemoPage() {
  // PageView fires automatically on mount (consistent with the rest of the site).
  const {
    trackCustomEvent,
    trackStandardEvent,
    trackStandardEventTo
  } = useMetaPixel();
  // Countdown for the floating $49/mo offer bar (resets at local midnight).
  const {
    hours,
    minutes,
    seconds
  } = useMidnightCountdown();
  const pad = (n: number) => n < 10 ? `0${n}` : n;
  // Standard Meta product identity used for ViewContent / AddToCart.
  const PRODUCT_DATA = {
    content_name: '48 Hrs AI Cash Machine',
    content_ids: ['48hrs-ai-cash-machine'],
    content_type: 'product',
    value: 147,
    currency: 'USD'
  };
  // Fire the standard funnel events (PageView, ViewContent, AddToCart) whenever
  // a visitor clicks ANY CTA button on the page — routed to EVERY CTA dataset
  // configured for the current route.
  const fireCtaPixelEvents = () => {
    for (const ctaPixel of getActiveCtaPixelIds()) {
      trackStandardEventTo(ctaPixel, 'PageView');
      trackStandardEventTo(ctaPixel, 'ViewContent', PRODUCT_DATA);
      trackStandardEventTo(ctaPixel, 'AddToCart', PRODUCT_DATA);
    }
  };
  // Floating $147 offer bar (mirrors the /147 sticky bar). It stays hidden and
  // only slides in once the 5-minute time gate elapses and the full page
  // reveals (see `revealed` below). Once shown it stays sticky for the rest of
  // the visit. The wiring lives in an effect further down that keys off
  // `revealed` (declared after the time-gate state).
  const [showOfferBar, setShowOfferBar] = useState(false);
  const handleOfferCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    trackCustomEvent('DemoOfferBarClick', {
      offer: '147'
    });
    fireCtaPixelEvents();
    window.location.href = OFFER_147_CHECKOUT_URL;
  };
  // Floating "Agregar al carrito" button that rides at the bottom of the page
  // through to the footer. It appears once the visitor scrolls past the first
  // video into the offer, so it accompanies them down to the footer CTA.
  const [showCartBar, setShowCartBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowCartBar(window.scrollY > 700);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleCartCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    trackCustomEvent('DemoAddToCart', {
      offer: '147',
      source: 'floating_cart'
    });
    fireCtaPixelEvents();
    window.location.href = OFFER_147_CHECKOUT_URL;
  };
  const handleVideoCtaCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    trackCustomEvent('DemoVideoCtaClick', {
      offer: '147',
      source: 'under_first_video'
    });
    fireCtaPixelEvents();
    window.location.href = OFFER_147_CHECKOUT_URL;
  };
  // Affiliate capture: read ?affiliate / ?ref / ?a from the URL and persist it
  // to a cookie (30 days) + localStorage so the referral survives navigation
  // to checkout. Runs once on mount for the /demo experience.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const aff = p.get('affiliate') || p.get('ref') || p.get('a');
      if (aff) {
        document.cookie = 'aff=' + encodeURIComponent(aff) + '; max-age=2592000; path=/; SameSite=Lax';
        try {
          localStorage.setItem('aff', aff);
        } catch (e) {}
      }
    } catch (e) {}
  }, []);
  // Fire the custom "Demo" pixel event once when the page loads, plus the
  // standard ViewContent (PageView already fires on pixel init).
  const demoFiredRef = useRef(false);
  useEffect(() => {
    if (demoFiredRef.current) return;
    demoFiredRef.current = true;
    trackCustomEvent('Demo');
    trackStandardEvent('ViewContent', PRODUCT_DATA);
  }, [trackCustomEvent, trackStandardEvent]);
  // TIME GATE: everything from below the PASO #1 video (Paso 2, Paso 3,
  // Testimonios, and the embedded offer) stays HIDDEN behind a 2-minute
  // countdown. Once the timer elapses the page unlocks and a cookie is dropped
  // so returning visitors load fully unlocked — it stays open unless the
  // visitor clears the cookie.
  const REVEAL_SECONDS = 120;
  const [secondsLeft, setSecondsLeft] = useState(() => hasUnlockedCookie() ? 0 : REVEAL_SECONDS);
  const revealed = secondsLeft <= 0;
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setInterval(() => {
      setSecondsLeft(s => s <= 1 ? 0 : s - 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [secondsLeft]);
  // Once the page has fully revealed, drop the cookie so future visits load
  // the complete page immediately.
  useEffect(() => {
    if (revealed) setUnlockedCookie();
  }, [revealed]);
  // Reveal the floating $147 bar only when the 5-minute time gate elapses
  // (i.e. when the whole page finishes loading). Returning visitors whose
  // cookie unlocks the page immediately see the bar right away too.
  useEffect(() => {
    if (revealed) setShowOfferBar(true);
  }, [revealed]);
  // Auto-size the embedded /49 offer iframe to its content height so the whole
  // offer flows into the page with NO inner scroll. The embedded page posts
  // its document height (see CheckoutBundlePage) and we grow the iframe to match.
  const offerFrameRef = useRef<HTMLIFrameElement>(null);
  const [offerHeight, setOfferHeight] = useState<number>(2400);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data;
      if (!data || data.type !== 'demo49:height') return;
      const h = Number(data.height);
      if (Number.isFinite(h) && h > 0) setOfferHeight(Math.ceil(h));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // SHARED SCARCITY COUNT — single source of truth for "members claimed". It
  // starts at the same baseline the standalone meter uses and ticks up by one
  // EVERY time a sales notification pops (see SalesNotifications' onShow), so
  // the pop-ups and the scarcity meter rise together. Capped at MAX_CLAIMED so
  // the offer never fully sells out.
  const [claimed, setClaimed] = useState(START_CLAIMED);
  const handleSale = () => {
    setClaimed(c => Math.min(MAX_CLAIMED, c + 1));
  };
  // Push the shared count into the embedded /147 offer iframe so its inline
  // scarcity meter rises in lockstep with the top meter and the pop-ups.
  useEffect(() => {
    const frame = offerFrameRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage({
      type: 'demo:claimed',
      claimed
    }, '*');
  }, [claimed]);
  // SALES POPS ARMING — the social-proof notifications no longer start on a
  // fixed timer. They arm the moment the visitor scrolls the PASO #1 video so
  // it's at least HALF out of view, then the first card pops almost instantly.
  const firstVideoRef = useRef<HTMLDivElement>(null);
  const [salesArmed, setSalesArmed] = useState(false);
  useEffect(() => {
    const el = firstVideoRef.current;
    if (!el) return;
    // Fire once the video is 50% out of view (i.e. ≤50% visible). Using an
    // IntersectionObserver keyed at the 0.5 threshold means "half out of view".
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.intersectionRatio <= 0.5) {
          setSalesArmed(true);
          observer.disconnect();
          break;
        }
      }
    }, {
      // Watch the full 0→1 range so we catch the moment it crosses half.
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen flex flex-col bg-[#f0f2f5] text-[#1c1e21] font-['system-ui','-apple-system','BlinkMacSystemFont','Segoe_UI','Roboto','Helvetica','Arial','sans-serif']" data-id="element-434" data-mp="f6dmr8kattbi">
      {/* BREAKING-NEWS TICKER — red scrolling banner at the very top, above the
           header. Two identical copies inside the track make the marquee loop
           seamlessly. */}
      <div className="w-full overflow-hidden bg-[#FD0200] text-white py-2 select-none" role="marquee" aria-label="La solución no es trabajar más. Es tener el sistema correcto." data-id="element-435" data-mp="f4sgm5gbjs1">
        <div className="flex w-max animate-marquee whitespace-nowrap will-change-transform" data-id="element-436" data-mp="3d0jybp3cofht">
          {[0, 1].map(copy => <div key={copy} aria-hidden={copy === 1} className="flex shrink-0 items-center" data-id="element-437" data-mp="3i0pr58lcw81h">
              {Array.from({
            length: 4
          }).map((_, i) => <span key={i} className="flex items-center text-[13px] font-extrabold uppercase tracking-[0.5px]" data-id="element-438" data-mp="3912o6iczau6b" data-mpi={i}>
                  La solución no es trabajar más. Es tener el sistema correcto.
                  <span aria-hidden="true" className="mx-5 text-white/70" data-id="element-439" data-mp="3mzj675gak73r">
                    •
                  </span>
                </span>)}
            </div>)}
        </div>
      </div>

      {/* FOUNDER / TRUST ROW — pinned as a compact header at the top of the
                                                                                                                                                                                                         page: founder avatar, bold name with verified check, and a green
                                                                                                                                                                                                         Trustpilot rating chip. */}
      <header className="w-full bg-[#f0f2f5] sticky top-0 z-50 shadow-[0_1px_0_rgba(0,0,0,0.06)]" data-id="element-440" data-mp="eof6d4zxp9k3">
        <div className="w-full max-w-[720px] mx-auto px-5 pt-6 pb-2.5 flex items-center justify-center gap-3" data-id="element-441" data-mp="and9uvxejblt">
          <div className="shrink-0" data-id="element-442" data-mp="on4sj9yv5g65">
            <img src="https://cdn.magicpatterns.com/uploads/3N8o444mVrfs4q2MWiGfdP/Screenshot_2026-07-02_at_3.43.21_PM.png" alt="Gabriel Beltrán — Fundador" width={36} height={36} decoding="async" className="w-9 h-9 rounded-full object-cover" data-id="element-443" data-mp="oo63zfjsk4oe" />
          </div>
          <div className="min-w-0" data-id="element-444" data-mp="4mcughvttv6b">
            <div className="flex items-center gap-1" data-id="element-445" data-mp="4nea6znsf5ud">
              <span className="font-bold text-[13px] leading-tight text-[#050505] truncate" data-id="element-446" data-mp="9keeb512tyiu">
                Gabriel Beltrán
              </span>
              <span className="shrink-0 inline-flex items-center justify-center w-[13px] h-[13px] rounded-full bg-[#1877F2]" data-id="element-447" data-mp="eig03xpw0by0">
                <CheckIcon className="w-2 h-2 text-white" strokeWidth={3} data-id="element-448" data-mp="ij9n8ovi9mpu" />
              </span>
            </div>
            <p className="text-[11px] text-[#65676b] leading-tight" data-id="element-449" data-mp="sjils9cyzk8e">
              Fundador · Heylloyd
            </p>
          </div>
          <div className="shrink-0 text-left" data-id="element-450" data-mp="xbjvpomo0yv9">
            <div className="flex items-center gap-[2px] justify-start mb-0.5" data-id="element-451" data-mp="xcuypm8ae2lb">
              {Array.from({
              length: 5
            }).map((_, i) => <span key={i} className="inline-flex items-center justify-center w-[13px] h-[13px] rounded-[2px] bg-[#00B67A]" data-id="element-452" data-mp="16crtm5l9rxot" data-mpi={i}>
                  <StarIcon className="w-2 h-2 text-white fill-white" data-id="element-453" data-mp="2vtdn0a0z2099" />
                </span>)}
            </div>
            <p className="text-[10.5px] text-[#65676b] leading-none" data-id="element-454" data-mp="2qrwkyb6r47r3">
              <span className="font-bold text-[#050505]" data-id="element-455" data-mp="2msnubf70cpzr">4.8</span> · 1600+
              miembros
            </p>
          </div>
        </div>
      </header>

      {/* MAIN — on mobile, start content right under the banner (no vertical
             centering) to avoid a large empty gap; on larger screens keep it
             vertically centered so short pre-reveal content sits mid-viewport. */}
      <main className="w-full flex-1 flex flex-col justify-start sm:justify-center" data-id="element-456" data-mp="35pgln5wm7grg">
        <div className="w-full max-w-[720px] mx-auto px-5 pt-4 pb-8 sm:py-8" data-id="element-457" data-mp="30s6zq08fpn8d">
          {/* HEADLINE + SUBHEADLINE */}
          <h2 className="text-center font-['Anton'] uppercase tracking-[0.5px] leading-[1.08] text-[35px] text-[#050505] mb-4" data-id="element-458" data-mp="226uelac2ov1">
            Me volví loco y estoy vendiendo mi academia completa de{' '}
            <span className="line-through decoration-[#FD0200] decoration-[3px]" data-id="element-459" data-mp="g88fcdu3vfyh">
              $1997
            </span>{' '}
            POR <span className="text-[#FD0200]" data-id="element-460" data-mp="l84i69r2h1cg">SOLO $27!</span>
          </h2>

          {/* HEADLINE */}
          <h1 className="text-center text-[clamp(20px,4.4vw,24px)] font-bold tracking-[-0.01em] leading-[1.2] mb-2" data-id="element-461" data-mp="7csqycc4dt0a">
            <span className="bg-gradient-to-r from-[#1877F2] to-[#5AB2FF] bg-clip-text text-transparent" data-id="element-462" data-mp="3tm3fbeprysrj">
              Paso 1:
            </span>{' '}
            <span className="text-[#050505]" data-id="element-463" data-mp="2h3oo18pty0a">Mira este video primero</span>
          </h1>

          <p className="text-[12px] text-[#65676b] leading-[1.35] mb-6 max-w-[560px] mx-auto text-center" data-id="element-464" data-mp="3kmgn9jixqzb5">
            Míralo completo — te explica cómo se hace dinero en línea, para
            quién es esto y cuáles son los próximos pasos.
          </p>

          {/* PASO #1 VIDEO — wrapped so we can watch when it scrolls half out of
           view and arm the sales pops at that moment. */}
          <div ref={firstVideoRef} data-id="element-465" data-mp="37e7rdlzgu6cb">
            <VideoCard id={PANDA_ID} src={PANDA_SRC} title="48 Hrs AI Cash Machine — Demo" priority data-id="element-466" data-mp="37b22a6qu74gl" />
          </div>

          {/* CTA UNDER THE FIRST VIDEO — hidden until the 2-minute countdown
           elapses, revealing together with the rest of the page. */}
          {revealed && <div className="mt-6 flex justify-center" data-id="element-467" data-mp="3r02p9pe5l45b">
              <a href="#" onClick={handleCartCheckout} aria-label="Obtén acceso ahora" className="group relative isolate overflow-hidden flex w-full max-w-[460px] items-center justify-center gap-2.5 rounded-lg bg-[#3FAC00] hover:bg-[#43B800] p-4 text-[#ffffff] font-extrabold text-[20px] tracking-tight transition-all duration-150 hover:-translate-y-0.5 active:translate-y-[2px] shadow-[0_4px_0_#2C7A00,0_1px_3px_rgba(0,0,0,0.07)] active:shadow-[0_2px_0_#2C7A00,0_1px_2px_rgba(0,0,0,0.07)]" data-id="element-468" data-mp="3m2kqgrtuo5b1">
                {/* Top gloss — white-to-transparent across the upper half */}
                <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-lg bg-gradient-to-b from-white/35 to-transparent" data-id="element-469" data-mp="1jtc4tq5ur84b" />
                {/* Angled sheen band that sweeps forward and back, then pauses */}
                <span aria-hidden="true" className="cta-sheen pointer-events-none absolute -inset-y-8 left-0 w-[26%] bg-gradient-to-r from-transparent via-white/45 to-transparent blur-[2px]" data-id="element-470" data-mp="ww0ixxmywx6q" />
                <ShoppingCartIcon className="relative z-10 w-5 h-5 shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-471" data-mp="1atxgaw98x7mg" />
                <span className="relative z-10 [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-472" data-mp="2hb2fris9cmss">
                  Obtén Acceso Ahora
                </span>
              </a>
            </div>}

          {/* TIME GATE (locked state) — everything below the PASO #1 video is
           hidden until the 2-minute countdown elapses. Show a friendly Spanish
           notice with a live countdown in its place. */}
          {!revealed && <div className="mt-6 flex flex-col items-center text-center" data-id="element-473" data-mp="ue9n8vtwgg1">
              <div className="inline-flex items-center gap-1.5 text-[#65676b]" data-id="element-474" data-mp="3s1gwxp8jq2kz">
                <ClockIcon className="w-3.5 h-3.5 text-[#1877F2]" data-id="element-475" data-mp="3o16v8orsdybx" />
                <p className="text-[12px] font-semibold text-[#050505]" data-id="element-476" data-mp="3j1twp6rjz2ti">
                  Los proximos pasos se abriran en 2 minutos
                </p>
              </div>
              <div aria-live="polite" className="mt-1.5 font-['Anton'] text-[22px] leading-none tracking-[0.5px] text-[#1877F2] tabular-nums" data-id="element-477" data-mp="3e2zeo71etiag">
                {pad(Math.floor(secondsLeft / 60))}:{pad(secondsLeft % 60)}
              </div>
            </div>}

          {/* TIME GATE — PASO #2 → Testimonios unlock once the 2-minute
           countdown elapses (or immediately for returning unlocked visitors). */}
          {revealed && <>
              {/* PASO #2 */}
              <h2 className="text-center text-[clamp(18px,4vw,22px)] font-bold tracking-[-0.01em] leading-[1.2] mt-12 mb-4" data-id="element-478" data-mp="1qwj7816zdfd1">
                <span className="bg-gradient-to-r from-[#1877F2] to-[#5AB2FF] bg-clip-text text-transparent" data-id="element-479" data-mp="1mueingve38x9">
                  Paso 2:
                </span>{' '}
                <span className="text-[#050505]" data-id="element-480" data-mp="1cxjjcvral4hx">Dentro de la academia</span>
              </h2>
              <VideoCard id={PASO2_ID} src={PASO2_SRC} title="48 Hrs AI Cash Machine — Dentro De La Academia" data-id="element-481" data-mp="17y6ts391djbn" />

              {/* PASO #3 */}
              <h2 className="text-center text-[clamp(18px,4vw,22px)] font-bold tracking-[-0.01em] leading-[1.2] mt-12 mb-1.5" data-id="element-482" data-mp="32by1gx7t1t4g">
                <span className="bg-gradient-to-r from-[#1877F2] to-[#5AB2FF] bg-clip-text text-transparent" data-id="element-483" data-mp="2ybnxtr7hjesr">
                  Paso 3:
                </span>{' '}
                <span className="text-[#050505]" data-id="element-484" data-mp="p1y8xepaxlan">
                  Costo operativo del negocio
                </span>
              </h2>
              <p className="text-center text-[15px] text-[#65676b] leading-[1.35] mb-4" data-id="element-485" data-mp="ttpt97hv6usc">
                Porque cuentas claras conservan la amistad 😊
              </p>
              <VideoCard id={PASO3_ID} src={PASO3_SRC} title="48 Hrs AI Cash Machine — Costo Operativo Del Negocio" data-id="element-486" data-mp="1494cjtgdw0v" />

              {/* TESTIMONIALS */}
              <h2 className="text-[clamp(17px,3.6vw,20px)] font-bold tracking-[-0.01em] leading-tight mt-12 mb-4 text-[#050505] text-center" data-id="element-487" data-mp="a4eakqmwuodg">
                Resultados Reales
              </h2>
              <VideoCard id={TESTIMONIAL_ID} src={TESTIMONIAL_SRC} title="48 Hrs AI Cash Machine — Testimonio" data-id="element-488" data-mp="1lkchooyj30c1" />

              {/* SCARCITY — limited to 100 lucky members; price reverts to
               $1,997 once full, plus a countdown timer. */}
              <div className="mt-10" data-id="element-489" data-mp="3ijfahj5sxf3t">
                <ScarcityCountdown claimed={claimed} data-id="element-490" data-mp="3imjmh2pidf51" />
              </div>

              {/* $147 offer page embedded inline under the Testimonios
               video. `embed=1` hides the sticky red offer bar so the page
               flows straight into the demo. */}
              <div className="mt-6 -mx-5 sm:mx-0" data-id="element-491" data-mp="33wcejgx4neil">
                <iframe ref={offerFrameRef} src="/147?embed=1" title="Oferta $27 — 48 Hrs AI Cash Machine" scrolling="no" className="block w-full border-0 sm:rounded-2xl bg-white" style={{
              height: offerHeight
            }} loading="lazy" onLoad={() => {
              trackCustomEvent('DemoOfferView', {
                offer: '147',
                source: 'testimonials'
              });
              // Seed the freshly-loaded offer iframe with the current
              // shared count so its meter starts in sync immediately.
              offerFrameRef.current?.contentWindow?.postMessage({
                type: 'demo:claimed',
                claimed
              }, '*');
            }} data-id="element-492" data-mp="2ywhikyyhvyqo" />
              </div>
            </>}
        </div>
      </main>

      {/* SALES SOCIAL-PROOF POPS — armed only once the visitor scrolls the PASO
                                                                                                                                #1 video half out of view (salesArmed), so the first pop lands right
                                                                                                                                as they scroll past it. Each pop bumps the shared count via onShow. */}
      <SalesNotifications enabled armed={salesArmed} position="above-cta" ctaVisible={showCartBar} onShow={handleSale} data-id="element-493" data-mp="e9epmyi58b62" />

      {/* FOOTER */}
      <footer className="mt-auto bg-[#f0f2f5] py-4 text-[11px] text-[#606770]" data-id="element-494" data-mp="4hs3i749oroj">
        <div className="max-w-[720px] mx-auto px-5" data-id="element-495" data-mp="h85l1bvdk1c">
          <p className="text-center text-[10px] text-[#606770]" data-id="element-496" data-mp="x5xqz1mpgus2">
            <span className="text-[#8a8d91]" data-id="element-497" data-mp="x5egu73araol">
              © {new Date().getFullYear()} True Marketer LLC. Todos los
              derechos reservados.
            </span>
          </p>

          <LegalLinks containerClassName="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 mt-3 text-[#606770]" linkClassName="hover:text-[#1877F2] mx-1.5 underline-offset-2 hover:underline" data-id="element-498" data-mp="o3y8s5zyfknj" />

          {/* RESULTS DISCLAIMER */}
          <p className="text-center text-[10px] leading-[1.4] text-[#9aa0a6] mt-4 max-w-[560px] mx-auto" data-id="element-499" data-mp="33vm1tqbukt12">
            Los resultados mostrados no son típicos. Los resultados individuales
            varían según el esfuerzo, experiencia y mercado de cada persona.
          </p>
        </div>
      </footer>

      {/* FLOATING ADD-TO-CART BAR — the entire bar IS the button now. Rides the
                                                                                                                            bottom of the page down to the footer and appears once the visitor
                                                                                                                            scrolls into the offer. Carries the premium glossy red plastic look. */}
      <a href="#" onClick={handleCartCheckout} aria-label="Haz clic para obtener acceso — solo $27" aria-hidden={!showCartBar} style={{
      backgroundImage: 'linear-gradient(180deg, #FD0200 0%, #AA0E13 100%)',
      boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.55), inset 0 -4px 10px rgba(120,6,4,0.55), 0 -6px 24px rgba(0,0,0,0.18)'
    }} className={`group fixed bottom-0 inset-x-0 z-[70] isolate flex w-full items-center justify-center gap-2.5 overflow-hidden px-5 py-6 text-white transition-all duration-300 ease-out hover:brightness-[1.04] active:brightness-[0.97] ${showCartBar ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-500" data-mp="2g5dsj8d3vh1z">
        {/* Top gloss — the plastic highlight across the upper half */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[46%] bg-gradient-to-b from-white/55 to-transparent" data-id="element-501" data-mp="231jc6wtlkfph" />
        {/* Angled sheen band that sweeps forward and back, then pauses */}
        <span aria-hidden="true" className="cta-sheen pointer-events-none absolute -inset-y-8 left-0 w-[26%] bg-gradient-to-r from-transparent via-white/45 to-transparent blur-[2px]" data-id="element-502" data-mp="2csno1nnz7kg3" />
        <ShoppingCartIcon className="relative z-10 w-5 h-5 shrink-0 drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" data-id="element-503" data-mp="2ltlsu868nvzq" />
        <span className="relative z-10 flex flex-col items-center leading-tight text-center" data-id="element-504" data-mp="35bq5ezqoc11h">
          <span className="font-extrabold text-[18px] sm:text-[20px] tracking-tight [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]" data-id="element-505" data-mp="3ac4d2god58yz">
            HAZ CLIC PARA OBTENER ACCESO
          </span>
          <span className="inline-flex items-center gap-2 text-[14px] font-normal text-white/90 [text-shadow:0_1px_1px_rgba(0,0,0,0.3)]" data-id="element-506" data-mp="30d52gsk1f39c">
            solo $27
            <span aria-hidden="true" className="text-white/50" data-id="element-507" data-mp="2rejqjg92mmhj">
              |
            </span>
            <span className="font-semibold" data-id="element-508" data-mp="2cpni3i33r7ge">
              ¡Solo quedan {Math.max(0, 100 - claimed)}!
            </span>
          </span>
        </span>
      </a>

      {/* Floating WhatsApp CTA — labeled pill, bottom-right. Sits above the
                                                                                                                            add-to-cart bar when that bar is visible. */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" aria-label="Continúa la conversación por WhatsApp" className={`fixed right-6 z-[80] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-900/30 transition-all duration-300 hover:scale-105 hover:bg-[#20bd5a] focus:outline-none focus:ring-4 focus:ring-emerald-300 ${showCartBar ? 'bottom-[112px]' : 'bottom-6'}`} data-id="element-509" data-mp="2o5tj7kd67jnf">
        <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0 fill-white" aria-hidden="true" data-id="element-510" data-mp="20fh1jhs4i8jn">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-511" data-mp="1vhnvcoqtlejd" />
        </svg>
      </a>
    </div>;
}